import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { ManagedMilestone } from "./ManagedMilestone";
import {
  deleteMilestone,
  fetchManagedMilestones,
  renameMilestone,
} from "./milestoneManagementApi";

interface MilestoneManagementState {
  milestones: ManagedMilestone[];
  status: "idle" | "loading" | "failed";
  mutating: boolean;
  error: string | null;
}

const initialState: MilestoneManagementState = {
  milestones: [],
  status: "idle",
  mutating: false,
  error: null,
};

const milestoneManagementSlice = createSlice({
  name: "milestoneManagement",
  initialState,
  reducers: {
    clearMilestoneManagementError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchManagedMilestones.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchManagedMilestones.fulfilled, (state, action) => {
        state.milestones = action.payload;
        state.status = "idle";
      })
      .addCase(fetchManagedMilestones.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as string) ?? "Failed to load milestones.";
      })
      .addCase(renameMilestone.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(renameMilestone.fulfilled, (state, action) => {
        state.mutating = false;
        const found = state.milestones.find(
          (m) => m.id === action.payload.milestoneId
        );
        if (found) {
          found.name = action.payload.name;
        }
      })
      .addCase(renameMilestone.rejected, (state, action) => {
        state.mutating = false;
        state.error = (action.payload as string) ?? "Failed to rename milestone.";
      })
      .addCase(deleteMilestone.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(deleteMilestone.fulfilled, (state, action) => {
        state.mutating = false;
        state.milestones = state.milestones.filter(
          (m) => m.id !== action.payload
        );
      })
      .addCase(deleteMilestone.rejected, (state, action) => {
        state.mutating = false;
        state.error = (action.payload as string) ?? "Failed to delete milestone.";
      });
  },
});

export const { clearMilestoneManagementError } =
  milestoneManagementSlice.actions;

export const selectManagedMilestones = (state: RootState) =>
  state.milestoneManagement.milestones;

export const selectManagedMilestonesLoading = (state: RootState) =>
  state.milestoneManagement.status === "loading";

export const selectMilestoneManagementMutating = (state: RootState) =>
  state.milestoneManagement.mutating;

export const selectMilestoneManagementError = (state: RootState) =>
  state.milestoneManagement.error;

export default milestoneManagementSlice.reducer;
